import {User} from '@perfectpost/perfect-post-common';
import {ConsoleLogger} from 'aws-amplify/utils';
import {Crisp} from 'crisp-sdk-web';
import {isFuture, parseISO} from 'date-fns';

import {VITE_BUILD_HASH} from '../config';

export class CrispClient {
  private iconVisible = false;
  private restoreOnCloseOnce = false;
  private listenersBound = false;
  private logger = new ConsoleLogger('CrispClient');
  private readonly buildHash = VITE_BUILD_HASH;

  constructor() {
    this.logger.info('Initializing CrispClient...');

    Crisp.configure('a30c50b9-400f-4fad-9cbc-b6142734b16b', {autoload: false});
    Crisp.setVacationMode(true);
    this.logger.info('Crisp configured with autoload: false and vacationMode: true');

    Crisp.load();
    this.logger.info('Crisp.load() called (preloading SDK)');

    // Évite tout flash éventuel d’icône après le load
    try {
      Crisp.chat.hide();
      this.logger.debug('Crisp.chat.hide() applied after load');
    } catch (err) {
      this.logger.warn('Error while hiding Crisp after load', err);
    }

    this.applyBuildHash();
    this.bindOnce();
  }

  /** Attache les listeners une seule fois */
  private bindOnce() {
    if (this.listenersBound) return;
    this.listenersBound = true;

    Crisp.chat.onChatClosed(() => {
      this.logger.debug('Crisp.chat.onChatClosed triggered');

      if (this.restoreOnCloseOnce) {
        this.logger.debug('Restoring hidden icon state after temporary open');
        try {
          Crisp.chat.hide();
        } catch (err) {
          this.logger.warn('Error while hiding Crisp on close', err);
        }
        Crisp.setVacationMode(true);
        this.iconVisible = false;
        this.restoreOnCloseOnce = false;
      }
    });

    this.logger.info('Crisp event listeners bound');
  }

  /* ---------- Contrôle explicite de l’icône ---------- */

  /** Affiche uniquement l’icône (fenêtre fermée) */
  public showIcon() {
    this.logger.debug('showIcon() called');
    Crisp.setVacationMode(false);
    try {
      Crisp.chat.close();
    } catch {
      /* empty */
    }
    Crisp.chat.show();
    this.iconVisible = true;
    this.logger.info('Crisp icon is now visible');
  }

  /** Cache complètement l’icône et toute UI */
  public hideIcon() {
    this.logger.debug('hideIcon() called');
    try {
      Crisp.chat.hide();
    } catch {
      /* empty */
    }
    Crisp.setVacationMode(true);
    this.iconVisible = false;
    this.logger.info('Crisp icon is now hidden');
  }

  /* ---------- Ouverture / fermeture ---------- */

  /**
   * Ouvre la fenêtre de chat sans modifier l’état d’affichage de l’icône.
   * - Si l’icône est visible, on ouvre normalement.
   * - Si elle est cachée, on ouvre temporairement puis on la re-cache à la fermeture.
   */
  public openChat(message?: string) {
    this.logger.debug('openChat() called');

    const iconWasVisible = this.iconVisible;
    const isChatOpened = typeof Crisp.chat.isChatOpened === 'function' ? Crisp.chat.isChatOpened() : false;

    if (isChatOpened) {
      this.logger.debug('Chat already opened');
      if (message) Crisp.message.setMessageText(message);
      return;
    }

    if (!iconWasVisible) {
      this.logger.debug('Icon hidden → temporarily enabling Crisp for chat');
      Crisp.setVacationMode(false);
      Crisp.chat.show(); // monte le widget sans le laisser visible durablement
      this.restoreOnCloseOnce = true;
    }

    setTimeout(() => {
      try {
        Crisp.chat.open();
        this.logger.info('Crisp chat opened');
        if (message) {
          Crisp.message.setMessageText(message);
          this.logger.debug('Message prefilled:', message);
        }
      } catch (err) {
        this.logger.error('Error while opening Crisp chat', err);
      }
    }, 0);
  }

  /** Ferme la fenêtre sans toucher à l’état de l’icône */
  public closeChat() {
    this.logger.debug('closeChat() called');
    try {
      Crisp.chat.close();
      this.logger.info('Crisp chat closed');
    } catch (err) {
      this.logger.warn('Error while closing Crisp chat', err);
    }
  }

  /* ---------- Tracking / infos utilisateur ---------- */

  public trackUser(user: User, premium: boolean) {
    this.logger.debug('trackUser() called', user);
    try {
      Crisp.user.setEmail(user.email);
      Crisp.user.setNickname(`${user.firstname} ${user.lastname}`);
      Crisp.session.setData(
        this.withBuildHash({
          user_id: user._id,
          plan: this.resolvePlan(user, premium),
          vanityName: user.vanityName ?? '',
        }),
      );
      this.logger.info('Crisp user tracked successfully');
    } catch (err) {
      this.logger.error('Error while tracking user in Crisp', err);
    }
  }

  public trackExtension(version: string) {
    this.logger.debug('trackExtension() called with', version);
    Crisp.session.setData(this.withBuildHash({extension_version: version}));
  }

  public trackEvent(event: string) {
    this.logger.debug('trackEvent() called with', event);
    Crisp.session.pushEvent(event);
  }

  public tryGpt() {
    this.logger.debug('tryGpt() called');
    Crisp.session.pushEvent('try_gpt');
  }

  /* ---------- Utils internes ---------- */

  private resolvePlan(user: User, premium: boolean): 'premium' | 'freemium' | 'free-trial' {
    if (this.isInFreeTrial(user)) return 'free-trial';
    return premium ? 'premium' : 'freemium';
  }

  private applyBuildHash() {
    if (!this.buildHash) {
      this.logger.debug('No build hash configured; skipping Crisp build hash tracking');
      return;
    }

    try {
      Crisp.session.setData(this.withBuildHash({}));
      this.logger.info(`Crisp build hash set to ${this.buildHash}`);
    } catch (err) {
      this.logger.warn('Error while setting Crisp build hash', err);
    }
  }

  private withBuildHash<T extends Record<string, unknown>>(data: T): T & {build_hash?: string} {
    if (!this.buildHash) return data;
    return {...data, build_hash: this.buildHash};
  }

  private isInFreeTrial(user: User): boolean {
    if (!user.earlyAdoptersPremiumExpireAt) return false;
    const expireAt = parseISO(user.earlyAdoptersPremiumExpireAt);
    if (Number.isNaN(expireAt.getTime())) return false;
    return isFuture(expireAt);
  }
}
